import { z } from "zod";

/**
 * Keyset pagination, not offset.
 *
 * The people and signal lists are ordered by a timestamp that keeps moving as
 * collection runs, so an offset page would skip and repeat rows while someone
 * scrolls. The cursor encodes the sort key and id of the last row, which is
 * stable and lets the index carry the whole query.
 */
export const CursorSchema = z.string().min(1).max(200);

export const PageQuerySchema = z.object({
  cursor: CursorSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PageQuery = z.infer<typeof PageQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    /** Total matching rows, for the footer count the tables show. */
    total: z.number().int().nonnegative(),
  });
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  total: number;
}

/** Cursors are opaque to clients but are just a sort key and id. */
export function encodeCursor(sortKey: string, id: string): string {
  return Buffer.from(`${sortKey}|${id}`, "utf8").toString("base64url");
}

export function decodeCursor(cursor: string): { sortKey: string; id: string } | null {
  try {
    const decoded = Buffer.from(cursor, "base64url").toString("utf8");
    const separator = decoded.lastIndexOf("|");
    if (separator < 1) return null;
    const sortKey = decoded.slice(0, separator);
    const id = decoded.slice(separator + 1);
    if (!sortKey || !id) return null;
    return { sortKey, id };
  } catch {
    return null;
  }
}
